/* eslint-disable prefer-rest-params */
import RequestService from 'common/services/api/RequestService';
import { apiRoots } from 'common/services/api/apiConstants';
import { isNullOrUndefined, isValidJsonString } from 'common/util/utils';
import commonSearchQueryBuilder from 'common/services/search/commonSearchQueryBuilder';
import { QuickMarkType } from '../../store/common/constants';
import { ExportFormat, OptionsMenuActionsName } from '../../store/optionsMenu/types';
import { ExplanationOfGradesRequestStatus, ExplanationOfGradesStatus } from '../../features/table/types';
import { CommentTypes } from '../../features/commentsList/services/commentsService';
import { CriteriaLibraries } from '../setMarksServices';
import { AssessmentRunSessionStatus, ContextDisplayStatus } from '../../store/context/reducer';
import { RatingControl } from '../../features/speakingTestSubmit/reducer';
import { FlaggingEvent } from '../../features/speakingTestIdVerification/types';
import { GRADING_PARTY_INDEX } from '../../store/common/search/constants';
import { OriginalityReportAccessChange } from '../../features/table/actions';

export const INVALID_PARAMETERS_ERROR = 'Invalid parameters passed';

const handleInvalidArguments = (...args: string[]) => {
    console.error(INVALID_PARAMETERS_ERROR, args);
    return Promise.reject({
        message: INVALID_PARAMETERS_ERROR,
        arguments: args,
    });
};

interface AnyDataObject {
    [propName: string]: any;
}

let requestService: RequestService;

export const configureApiService = (reqService: RequestService): void => {
    requestService = reqService;
};

const apiService = {
    async updateOriginalityReportAccess(
        assessmentRunId: number,
        changes: OriginalityReportAccessChange[]
    ) {
        if (!(assessmentRunId > 0 && Array.isArray(changes) && changes.length > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}SetAntiplagiarismReportsShareStatus`;
        const postData = {
            assessmentRunId,
            jsonString: JSON.stringify(changes),
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },
    async fetchWorkspace(workspaceId: string) {
        return require(`../../../docs/workspace/${workspaceId}.json`);
    },

    async fetchFilters() {
        return require('../../../docs/request-examples/filters.json');
    },

    async fetchSignedWebSocketUrl() {
        const url = `${apiRoots.ASSESSMENT}GetSignedWebSocketUrl`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async endImpersonation() {
        const url = `${apiRoots.ADMIN}EndImpersonation`;
        return requestService.executeRequest(
            requestService.createJsonPostRequestWithTokenObject(url, {})
        );
    },

    async updateCommitteeModeration(status: string, committeeId: number, comment?: string) {
        const url = `${apiRoots.ASSESSMENT}UpdateCommitteeModeration`;
        const postData: AnyDataObject = { status, committeeId };
        if (comment) {
            postData.comment = comment;
        }
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async fetchGradingWorkspaces(assessmentRunId: number) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}GetGradingWorkspaces?assessmentRunId=${assessmentRunId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async fetchListGradingParty(
        searchJSON: string,
        listChildTestsGradingParties?: boolean,
        questionId?: number,
        signal?: AbortSignal
    ) {
        const cleanJSON = commonSearchQueryBuilder.cleanQuery(searchJSON);
        let url = `${apiRoots.ASSESSMENT}ListGradingParty?type=${GRADING_PARTY_INDEX}`;
        url = `${url}&searchJSON=${cleanJSON}`;
        if (listChildTestsGradingParties) {
            url = `${url}&listChildTestsGradingParties=${listChildTestsGradingParties}`;
        }
        if (questionId > 0) {
            url = `${url}&questionId=${questionId}`;
        }
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal),
        );
    },

    async fetchMultiListGradingParty(searchJSONs: string[], questionId?: number, signal?: AbortSignal) {
        const multiQuery = commonSearchQueryBuilder.encodeQueriesForMultiSearch(searchJSONs);
        let url = `${apiRoots.ASSESSMENT}ListGradingParty?type=${GRADING_PARTY_INDEX}`;
        url = `${url}&searchJSON=${multiQuery}`;
        if (questionId > 0) {
            url = `${url}&questionId=${questionId}`;
        }
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getMarkSchemeUploadTicket(
        assessmentRunId: number,
        obtainTickets: boolean,
        quantity: number
    ) {
        const url = `${apiRoots.ASSESSMENT}FileUploadGradingInstructions`;
        const postData: AnyDataObject = {
            assessmentRunId,
            obtainTickets,
            quantity,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async storeMarkSchemeFile(
        assessmentRunId: number,
        ticket: string,
        title: string
    ) {
        const url = `${apiRoots.ASSESSMENT}StoreUploadedGradingInstructions`;
        const postData: AnyDataObject = {
            assessmentRunId,
            ticket,
            title,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async uploadMarkSchemeFile(
        assessmentRunId: number,
        ticket: string,
        file: File
    ) {
        const url = (
            `${apiRoots.ASSESSMENT}FileUploadGradingInstructions?assessmentRunId=${assessmentRunId}&uploadFile=true&ticket=${ticket}&forceHTML=true`
        );
        const formData: FormData = new FormData();
        formData.append('files[]', file);
        return requestService.executeRequest(
            requestService.createBinaryDataPostRequestObject(url, formData)
        );
    },

    async removeMarkSchemeFile(assessmentRunId: number) {
        const url = `${apiRoots.ASSESSMENT}RemoveGradingInstructions`;
        const postData: AnyDataObject = {
            assessmentRunId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async fetchEvaluatorNotes(
        assessmentRunId: number,
        partyId: number,
        questionId?: number,
        signal?: AbortSignal
    ) {
        if (!(partyId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        let url: string = `${apiRoots.ASSESSMENT}GetEvaluatorNotes?assessmentRunId=${assessmentRunId}`
            + `&partyId=${partyId}`;
        if (questionId !== undefined) {
            url = `${url}&questionId=${questionId}`;
        }
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async storeEvaluatorNote(
        note: string,
        assessmentRunId: number,
        partyId: number,
        questionId?: number
    ) {
        if (!(partyId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.ASSESSMENT}StoreEvaluatorNote`;
        const postData: AnyDataObject = { assessmentRunId, partyId, note };

        if (questionId !== undefined) {
            postData.questionId = questionId;
        }

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async getScore(
        questionId: number,
        assessmentRunId: number,
        partyId: number,
        committeeId: number,
        signal?: AbortSignal
    ) {
        if (!(questionId > 0 && assessmentRunId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        let url: string = `${apiRoots.GRADING}GetScores?assessmentRunId=${assessmentRunId}&`
            + `questionId=${questionId}&partyId=${partyId}`;

        if (committeeId > 0) {
            url = `${url}&committeeId=${committeeId}`;
        }

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    /** Confirms the current submission's marking. Resolves to { success, message }. */
    async doneAndNextGraideMarking(
        assessmentRunId: number,
        partyId: number,
        questionId: number,
        excludeAiFeedback: boolean
    ) {
        if (!(assessmentRunId > 0 && partyId > 0 && questionId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.ASSESSMENT}DoneAndNextGraideMarking`;
        const postData: object = {
            assessmentRunId,
            partyId,
            questionId,
            ...(excludeAiFeedback ? { excludeAiFeedback: true } : {}),
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async getGraideMarkingSession(
        questionId: number,
        assessmentRunId: number,
        partyId: number,
        signal?: AbortSignal
    ) {
        if (!(questionId > 0 && assessmentRunId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url: string = `${apiRoots.GRADING}GetGraideMarkingSession?assessmentRunId=${assessmentRunId}&`
            + `questionId=${questionId}&partyId=${partyId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getEvaluatorScore(
        questionId: number,
        assessmentRunId: number,
        partyId: number,
        committeeId: number,
        signal?: AbortSignal
    ) {
        if (!(questionId > 0 && assessmentRunId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        let url: string = `${apiRoots.GRADING}GetEvaluatorScore?assessmentRunId=${assessmentRunId}&`
            + `questionId=${questionId}&partyId=${partyId}`;

        if (committeeId > 0) {
            url = `${url}&committeeId=${committeeId}`;
        }

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getUploadedFileInfo(
        assessmentRunId: number,
        partyId: number,
        questionId: number,
        signal?: AbortSignal
    ) {
        if (!(partyId > 0 && assessmentRunId > 0 && questionId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url: string = `${apiRoots.GRADING}GetUploadedFileInfo?assessmentRunId=${assessmentRunId}&`
            + `partyId=${partyId}&questionId=${questionId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getUserResponseData(
        assessmentRunId: number,
        partyId: number,
        contentRevisionId: number,
        signal?: AbortSignal
    ) {
        if (!(partyId > 0 && assessmentRunId > 0 && contentRevisionId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url: string = `${apiRoots.TESTPLAYER}GetUserResponse?questionId=${contentRevisionId}&`
            + `partyId=${partyId}&assessmentRunId=${assessmentRunId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getGradingHtmlContent(
        assessmentRunId: number,
        workspace: string,
        workspaceGroup: string,
        signal?: AbortSignal
    ) {
        if (
            !(
                workspace.length > 0
                && assessmentRunId > 0
                && workspaceGroup.length > 0
            )
        ) {
            return handleInvalidArguments(...arguments);
        }
        const url: string = `${apiRoots.GRADING}GetGradingHtmlContent?assessmentRunId=${assessmentRunId}&`
            + `workspace=${workspace}&workspaceGroup=${workspaceGroup}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async storeGradingHtmlContent(
        assessmentRunId: number,
        workspace: string,
        workspaceGroup: string,
        contentAsHTML: string
    ) {
        if (
            !(
                workspace.length > 0
                && assessmentRunId > 0
                && workspaceGroup.length > 0
            )
        ) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}StoreGradingHtmlContent`;
        const postData: object = {
            assessmentRunId,
            workspace,
            workspaceGroup,
            contentAsHTML,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async getAssessmentQuestionTypes(
        assessmentRunId: number,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}GetAssessmentQuestionTypes?assessmentRunId=${assessmentRunId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async saveExplanationOfGrades(
        assessmentRunId: number,
        partyId: number,
        committeeId?: number,
        content: string = null,
        explanationId?: number,
        userAssessmentId?: number
    ) {
        if (!(assessmentRunId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.ASSESSMENT}SaveExplanationOfGrades`;
        const postData: any = {
            assessmentRunId,
            partyId,
            content,
        };
        if (committeeId) {
            postData.committeeId = committeeId;
        }
        if (explanationId) {
            postData.explanationId = explanationId;
        } else {
            postData.userAssessmentId = userAssessmentId;
        }
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async updateExplanationOfGradesOrRequestStatus(
        assessmentRunId: number,
        userAssessmentId: number,
        studentId: number,
        committeeId: number,
        newExplanationStatus:
            | ExplanationOfGradesStatus
            | ExplanationOfGradesRequestStatus,
        sendToAllCandidates?: boolean,
    ) {
        if (!sendToAllCandidates && !(assessmentRunId > 0 && studentId > 0 && userAssessmentId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.ASSESSMENT}UpdateExplanationOfGradesStatus`;
        const postData: any = {
            assessmentRunId,
            studentId,
            userAssessmentId,
            committeeId,
            newExplanationStatus,
            sendToAllCandidates,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async editAssessmentRun(
        assessmentRunId: number,
        gradeExplanationEndTime: string
    ) {
        if (!gradeExplanationEndTime) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.ASSESSMENT}EditAssessmentRun?assessmentRunId=${assessmentRunId}`;
        const postData: any = { gradeExplanationEndTime };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async getExplanationOfGradesId(
        assessmentRunId: number,
        partyId: number,
        committeeId?: number,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        let url: string = `${apiRoots.ASSESSMENT}GetExplanationOfGradesId?assessmentRunId=${assessmentRunId}&`
            + `deliveringPartyId=${partyId}`;
        if (committeeId) {
            url = `${url}&committeeId=${committeeId}`;
        }
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchExplanationOfGrades(
        assessmentRunId: number,
        explanationId: number,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url: string = `${apiRoots.ASSESSMENT}GetExplanationOfGrades?assessmentRunId=${assessmentRunId}&`
            + `explanationId=${explanationId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getSubmittedExplanationOfGrades(
        assessmentRunId: number,
        explanationIds?: number[],
        signal?: AbortSignal
    ) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        let url = `${apiRoots.ASSESSMENT}GetSubmittedExplanationOfGrades?assessmentRunId=${assessmentRunId}`;
        url = `${url}&explanationIds=${explanationIds}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async studentSetExplanationStatusOpened(
        assessmentRunId: number,
        explanationIds?: number[]
    ) {
        if (
            !(
                assessmentRunId > 0
                && explanationIds
                && explanationIds.length > 0
            )
        ) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.ASSESSMENT}StudentSetExplanationStatusOpened`;
        const postData: any = {
            assessmentRunId,
            explanationIds,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async fetchMarkScheme(
        assessmentRunId: number,
        questionId: number,
        signal?: AbortSignal
    ) {
        if (!(questionId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url: string = `${apiRoots.GRADING}GetQuestionMarkScheme?assessmentRunId=${assessmentRunId}&`
            + `questionId=${questionId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchTest(assessmentRunId: number, signal?: AbortSignal) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.DASHBOARD}GetTest?assessmentRunId=${assessmentRunId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async resetScore(
        assessmentRunId: number,
        partyId: number,
        questionId: number,
        signal?: AbortSignal
    ) {
        if (!(questionId > 0 && partyId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}ResetScore`;
        const postData: object = {
            assessmentRunId,
            partyId,
            questionId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },

    async resetGrade(
        assessmentRunId: number,
        partyId: number,
        resetToAutomaticGrade?: boolean,
        signal?: AbortSignal
    ) {
        if (!(partyId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}ResetGrade`;
        const postData: object = {
            assessmentRunId,
            partyId,
            resetToAutomaticGrade,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },

    async setGrade(
        assessmentRunId: number,
        partyId: number,
        numericGradeValue: number,
        isManuallySet?: boolean
    ) {
        if (!(partyId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}SetGrade`;
        const postData: object = {
            assessmentRunId,
            partyId,
            numericGradeValue,
            isManuallySet,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },
    async setUserAssessmentFlagged(
        assessmentRunId: number,
        partyId: number,
        flag: boolean
    ) {
        if (!(assessmentRunId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}SetFlagForUserAssessmentEvaluation`;
        const postData: object = {
            assessmentRunId,
            flag,
            partyId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },
    async setScore(
        assessmentRunId: number,
        partyId: number,
        questionId: number,
        score: number
    ) {
        if (!(questionId > 0 && partyId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}SetScore`;
        const postData: object = {
            assessmentRunId,
            score,
            partyId,
            questionId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async setFlagUserQuestion(
        assessmentRunId: number,
        partyId: number,
        questionId: number,
        flag: boolean
    ) {
        const url = `${apiRoots.GRADING}SetFlagForUserQuestionEvaluation`;
        const postData: object = {
            assessmentRunId,
            flag,
            partyId,
            questionId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async fetchQuickMarks(
        assessmentRunId: number,
        partyId: number,
        questionId?: number,
        committeeId?: number,
        signal?: AbortSignal
    ) {
        if (!(partyId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        let queryString = `assessmentRunId=${assessmentRunId}&partyId=${partyId}`;
        if (questionId) {
            queryString = `${queryString}&questionId=${questionId}`;
        }

        if (committeeId > 0) {
            queryString = `${queryString}&committeeId=${committeeId}`;
        }

        const url = `${apiRoots.GRADING}GetQuickMarks?${queryString}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(
                url,
                signal
            )
        );
    },
    async getQuickMarkLibraries(
        assessmentRunId: number,
        partyId: number,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        const url = `${apiRoots.GRADING}GetQuickMarkLibraries?assessmentRunId=${assessmentRunId}&partyId=${partyId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(
                url,
                signal
            )
        );
    },
    async fetchCandidateScoreDetails(
        assessmentRunId: number,
        partyId: number,
        signal?: AbortSignal
    ) {
        if (!(partyId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        const queryString = `assessmentRunId=${assessmentRunId}&partyId=${partyId}`;
        const url = `${apiRoots.GRADING}GetCandidateScoreDetails?${queryString}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(
                url,
                signal
            )
        );
    },

    async addQuickMark(
        assessmentRunId: number,
        questionId: number,
        criteriaText: string,
        type: QuickMarkType,
        quickMarkLibraryType: CriteriaLibraries,
        score?: number,
        quickMarkLibraryId?: number,
    ) {
        const url = `${apiRoots.GRADING}AddQuickMark`;
        const postData: any = {
            assessmentRunId,
            questionId,
            criteriaText,
            type,
            quickMarkLibraryType,
        };
        if (!isNullOrUndefined(score)) {
            postData.score = score;
        }
        if (!isNullOrUndefined(quickMarkLibraryId)) {
            postData.quickMarkLibraryId = quickMarkLibraryId;
        }
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async applyQuickMark(
        assessmentRunId: number,
        deliveryPartyId: number,
        questionId: number,
        quickMarkId: number
    ) {
        const url = `${apiRoots.GRADING}ApplyQuickMark`;
        const postData: object = {
            assessmentRunId,
            questionId,
            deliveryPartyId,
            quickMarkId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async clearQuickMark(
        assessmentRunId: number,
        deliveryPartyId: number,
        questionId: number,
        quickMarkId: number
    ) {
        const url = `${apiRoots.GRADING}ClearQuickMark`;
        const postData: object = {
            assessmentRunId,
            questionId,
            deliveryPartyId,
            quickMarkId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async deleteQuickMark(assessmentRunId: number, quickMarkId: number) {
        const url = `${apiRoots.GRADING}DeleteQuickMark`;
        const postData: object = {
            assessmentRunId,
            quickMarkId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async updateQuickMarkLibrary(
        quickMarkId: number,
        quickMarkLibraryId: number
    ) {
        const url = `${apiRoots.GRADING}UpdateQuickMarkLibrary`;
        const postData: object = {
            quickMarkId,
            quickMarkLibraryId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async updateQuickMarkScore(
        assessmentRunId: number,
        quickMarkId: number,
        score: number
    ) {
        const url = `${apiRoots.GRADING}UpdateQuickMarkScore`;
        const postData: object = {
            assessmentRunId,
            score,
            quickMarkId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async updateQuickMarkText(
        assessmentRunId: number,
        quickMarkId: number,
        text: string
    ) {
        const url = `${apiRoots.GRADING}UpdateQuickMarkText`;
        const postData: object = {
            assessmentRunId,
            text,
            quickMarkId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async fetchListStudentQuestions(
        assessmentRunId: number,
        searchJSON: string,
        partyId?: number
    ) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        let url = `${apiRoots.GRADING}ListStudentQuestions?assessmentRunId=${assessmentRunId}`;
        url = `${url}&searchJSON=${searchJSON}`;

        if (partyId) {
            url = `${url}&partyId=${partyId}`;
        }

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async fetchAnnotations(query: string, signal?: AbortSignal) {
        const url = `${apiRoots.HYPOTHESIS}search?${query}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async fetchAnnotationGroups(assessmentRunId: number) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.HYPOTHESIS}groups?assessmentRunId=${assessmentRunId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async getGradingTabs(workspaceRef: string, assessmentRunId: number) {
        if (!(assessmentRunId > 0 && workspaceRef.length > 0)) {
            return handleInvalidArguments(...arguments);
        }
        let url = `${apiRoots.GRADING}GetGradingTabs?workspaceRef=${workspaceRef}`;
        url = `${url}&assessmentRunId=${assessmentRunId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async saveTab(title: string, tabParams: string, filterType: string) {
        let url = `${apiRoots.ASSESSMENT}CreateFilter?filterType=${filterType}`;
        url = `${url}&title=${title}&parameters=${tabParams}`;
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url)
        );
    },

    async updateGradingTab(filterId: number, title: string, tabParams: string) {
        let url = `${apiRoots.ASSESSMENT}UpdateFilter?filterId=${filterId}`;
        url = `${url}&title=${title}&parameters=${tabParams}`;
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url)
        );
    },

    async removeGradingTab(filterId: number) {
        const url = `${apiRoots.ASSESSMENT}RemoveFilter?filterId=${filterId}`;
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url)
        );
    },

    async removeCustomGradingTabs(
        workspaceRef: string,
        assessmentRunId: number,
        dataType?: string
    ) {
        let url = `${apiRoots.GRADING}DeleteGradingTabs?assessmentRunId=${assessmentRunId}`;
        url = `${url}&workspaceRef=${workspaceRef}`;
        if (dataType) {
            url = `${url}&dataType=${dataType}`;
        }
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url)
        );
    },

    async fetchGradingInfo(assessmentRunId: number) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}GetGradingInfo?assessmentRunId=${assessmentRunId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async fetchGradingWorkflow(assessmentRunId: number) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.ASSESSMENT}GetGradingWorkflow?assessmentRunId=${assessmentRunId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async syncWithExternalTest(assessmentRunId: number) {
        const url = `${apiRoots.ASSESSMENT}SyncAssessmentRunWithExternalTest`;
        const postData: object = {
            assessmentRunId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async getWorkspaceScheme(assessmentRunId: number, signal?: AbortSignal) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        let url = `${apiRoots.GRADING}GetGradingWorkspaces?assessmentRunId=${assessmentRunId}`;
        url = `${url}&noFilter=true`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },
    async saveWorkspaceScheme(assessmentRunId: number, content: string) {
        const url = `${apiRoots.ASSESSMENT}SetItemValue`;
        const postData: object = {
            objectId: assessmentRunId,
            itemProperty: 'workspace_scheme',
            value: content,
            flushCache: true,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },
    async resetWorkspaceScheme(assessmentRunId: number) {
        const url = `${apiRoots.GRADING}ResetWorkspaceScheme`;
        const postData: object = {
            assessmentRunId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async fetchPlagiarismReport(
        assessmentRunId: number,
        partyId: number,
        questionId: number
    ) {
        if (!(partyId > 0 && questionId > 0)) {
            return undefined;
        }
        const url = `${apiRoots.ASSESSMENT}GetAntiPlagiarismData?`
            + `assessmentRunId=${assessmentRunId}&questionId=${questionId}&partyId=${partyId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async getEulaAcceptance(
        assessmentRunId: number,
    ) {
        const url = `${apiRoots.ASSESSMENT}GetEulaAcceptance?`
            + `assessmentRunId=${assessmentRunId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async acceptEula(
        assessmentRunId: number,
    ) {
        const postData: object = {
            assessmentRunId,
        };
        const url = `${apiRoots.ASSESSMENT}AcceptEula`;

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async requestPlagiarismReportCheck(
        assessmentRunId: number,
        partyId: number,
        questionId: number,
        documentContentRevisionId: number
    ) {
        if (!(assessmentRunId > 0 && partyId > 0 && questionId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.ASSESSMENT}RequestAntiPlagiarismCheck?assessmentRunId=${assessmentRunId}`
            + `&questionId=${questionId}&documentContentRevisionId=${documentContentRevisionId}&partyId=${partyId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async fetchBandedOrCriteriaMarks(
        assessmentRunId: number,
        partyId: number,
        questionId: number,
        committeeId: number
    ) {
        if (!(assessmentRunId > 0 && partyId > 0 && questionId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        let url = `${apiRoots.ASSESSMENT}GetGradingUserResponse?assessmentRunId=${assessmentRunId}`
            + `&questionId=${questionId}&partyId=${partyId}`;

        if (committeeId > 0) {
            url = `${url}&committeeId=${committeeId}`;
        }

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async exportEmptyMarkingSheet(
        assessmentRunId: number,
        committeeId?: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.GRADING_XLS_STREAMABLE}ExportMarkingSheet`;
        const postData: object = committeeId && committeeId > 0
            ? {
                assessmentRunId,
                committeeId,
            }
            : {
                assessmentRunId,
            };
        return requestService.executeFileDownload(
            requestService.createFetchUrlWithQueryParams(url, postData, signal)
        );
    },

    async setCriteriaScore(
        assessmentRunId: number,
        partyId: number,
        questionId: number,
        criteriaId: string,
        score: number,
        criteriaType: CommentTypes,
        deleteGrading: boolean,
        allCriteriaMarked?: boolean,
        criterionFeedback?: string,
    ) {
        const url = `${apiRoots.ASSESSMENT}SetScoreForCriteria`;
        let extraParams = {};
        if (criteriaType === CommentTypes.CRITERIA_MARKING) {
            extraParams = { criteriaMarking: true };
        }
        if (criteriaType === CommentTypes.BANDED_MARKING || criteriaType === CommentTypes.RUBRICS_MARKING) {
            extraParams = { allCriteriaMarked };
        }
        const postData: object = {
            assessmentRunId,
            partyId,
            questionId,
            criteriaId,
            score,
            deleteGrading,
            newGrading: true,
            criterionFeedback,
            ...extraParams,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async lockExplanationOfGrades(
        contentItemId: number,
        assessmentRunId: number,
        explanationId: number,
        lockItem: boolean
    ) {
        const url = `${apiRoots.ASSESSMENT}LockExplanationOfGrades?contentItemId=${contentItemId}`
            + `&assessmentRunId=${assessmentRunId}&explanationId=${explanationId}&lockItem=${lockItem}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async applyDraft(
        assessmentRunId: number,
        draftId: number,
        reapplyThresholdsOnly: boolean,
        committeeId?: number,
    ) {
        const url = reapplyThresholdsOnly
            ? `${apiRoots.GRADING}ReapplyThresholds` : `${apiRoots.GRADING}ApplyDraft`;
        let postData: object = {
            assessmentRunId,
        };
        if (!reapplyThresholdsOnly) {
            postData = {
                ...postData,
                draftId,
            };
        }
        if (committeeId > 0) {
            postData = {
                ...postData,
                committeeId,
            };
        }
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async getDraft(
        draftId: number,
    ) {
        const url = `${apiRoots.GRADING}GetDraft?draftId=${draftId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async deleteDraft(
        draftId: number,
    ) {
        const url = `${apiRoots.GRADING}DeleteDraft`;
        const postData: object = { draftId };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async storeDraft(
        draftId: number,
        jsonString: string,
    ) {
        const url = `${apiRoots.GRADING}StoreDraft`;
        const postData: object = {
            draftId,
            jsonString,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async getOrCreateDraft(
        assessmentRunId: number,
        draftType: string,
    ) {
        const url = `${apiRoots.GRADING}GetOrCreateDraft?assessmentRunId=${assessmentRunId}&draftType=${draftType}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async getItemLocks(
        objectId: number
    ) {
        const url = `${apiRoots.ASSESSMENT}GetLocksOnItem?objectId=${objectId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },
    async lockItem(
        objectId: number,
        lockItem: boolean,
    ) {
        const url = `${apiRoots.ASSESSMENT}LockItem?objectId=${objectId}`
            + `&lockItem=${lockItem}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async restoreCriteriaScore(
        assessmentRunId: number,
        partyId: number,
        questionId: number
    ) {
        const url = `${apiRoots.ASSESSMENT}RestoreCriteriaScore`;
        const postData: object = {
            assessmentRunId,
            partyId,
            questionId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async getUploadTicketForExplanation(
        mimeType: string,
        fileName: string,
        fileSize: number,
        partyId: number,
        assessmentRunId: number
    ) {
        const url = `${apiRoots.ASSESSMENT}GetUploadTicketForExplanationAttachment`;
        const postData: object = {
            assessmentRunId,
            fileName,
            fileSize,
            mimeType,
            partyId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async uploadExplanationAttachmentSuccess(
        partyId: number,
        assessmentRunId: number,
        explanationId: number,
        ticket: string
    ) {
        const url = `${apiRoots.ASSESSMENT}UploadExplanationAttachmentSuccess`;
        const postData: object = {
            assessmentRunId,
            partyId,
            explanationId,
            ticket,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async removeExplanationAttachment(
        partyId: number,
        assessmentRunId: number,
        explanationId: number,
        attachmentId: number
    ) {
        const url = `${apiRoots.ASSESSMENT}RemoveExplanationAttachment`;
        const postData: object = {
            assessmentRunId,
            partyId,
            explanationId,
            attachmentId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async resendInvite(
        assessmentRunId: number,
        userId: number,
    ) {
        const url = `${apiRoots.ASSESSMENT}SendGraderInvitationEmail`;
        const postData: object = {
            assessmentRunId,
            userId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async getAssessmentRunExports(
        assessmentRunId: number,
        committeeId: number,
        signal?: AbortSignal
    ) {
        let url = `${apiRoots.ASSESSMENT}GetAssessmentRunExports?assessmentRunId=${assessmentRunId}`;

        if (committeeId > 0) {
            url = `${url}&committeeId=${committeeId}`;
        }

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async submitTask(
        assessmentRunId: number,
        format: string,
        repository: string,
        ticket: string,
        signal?: AbortSignal,
        downloadType?: string,
        params?: { [key: string]: unknown }
    ) {
        const url = `${apiRoots.ASYNC}SubmitTask`;

        let actionApi: string;

        switch (format) {
        case OptionsMenuActionsName.UPLOAD_GRADES_FROM_EXCEL:
            actionApi = 'ImportGrades';
            break;

        case OptionsMenuActionsName.MARKS_IMPORT_AS_EXCEL:
            actionApi = 'ImportMarks';
            break;

        case ExportFormat.TOTAL_MARKS_AND_GRADES_SUMMARY:
            actionApi = 'GenerateTotalMarksAndGradesSummary';
            break;

        case ExportFormat.JAGGED_ANALYSIS:
            actionApi = 'ExportRemarkRequests';
            break;
        case ExportFormat.MARKS_AND_CRITERIA:
            actionApi = 'GenerateMarksAndCriteriaExport';
            break;
        case ExportFormat.MULTIPLE_ATTEMPTS_CSV:
            actionApi = 'GenerateMultipleAttemptResultExport';
            break;

        case ExportFormat.ORIGINAL_SUBMISSIONS:
            actionApi = 'AssessmentOriginalSubmissionsExport';
            break;
        default:
            actionApi = 'UploadAssessmentRunResultExport';
        }

        let parameters: any = { format, assessmentRunId, downloadType };

        if (ticket) {
            parameters = { assessmentRunId, ticket };
        }

        if (params) {
            parameters = { ...parameters, ...params };
        }

        const postData: object = {
            ticket,
            repository,
            action: actionApi,
            parametersJSON: JSON.stringify(parameters),
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },
    async checkStatus(jobId: string, signal?: AbortSignal) {
        const url = `${apiRoots.ASYNC}CheckStatus`;
        const postData: object = {
            jobId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },

    async exportGrades(assessmentRunId: number, signal?: AbortSignal) {
        const url = `${apiRoots.EXPORT}xls/ExportGrades?assessmentRunId=${assessmentRunId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },
    async downloadCandidateReport(
        assessmentRunId: number,
        exportStudentResults: boolean,
        fileName?: string,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}csv/OXMEDCSVGeneration?assessmentRunId=${assessmentRunId}&`
            + `exportStudentResults=${exportStudentResults}`;

        return requestService.executeFileDownload(
            requestService.createFetchRequestObject(url, signal),
            fileName
        );
    },
    async closeTest(assessmentRunId: number, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}CloseTest`;
        const postData: object = {
            assessmentRunId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },
    async reopenGrading(
        assessmentRunId: number,
        committeeId: number,
        signal?: AbortSignal
    ) {
        let url = `${apiRoots.ASSESSMENT}ReopenFinalGrading`;
        let postData: object = {
            assessmentRunId,
        };

        if (committeeId && committeeId > 0) {
            url = `${apiRoots.ASSESSMENT}ReopenFinalGradingForCommittee`;
            postData = {
                committeeId,
                ...postData,
            };
        }
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },

    async deAnonymizeCandidates(assessmentRunId: number, signal?: AbortSignal) {
        const url = `${apiRoots.ASSESSMENT}grading/DeAnonymizeCandidates`;
        const postData: object = {
            assessmentRunId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },
    async getGradeImportExportUploadTicket(
        fileName: string,
        fileSize: number,
        assessmentRunId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.EXPORT}GetGradeImportExportUploadTicket`;
        const postData: object = {
            assessmentRunId,
            fileName,
            fileSize,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },
    async getMarkImportUploadTicket(
        fileName: string,
        fileSize: number,
        assessmentRunId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.EXPORT}GetMarkImportUploadTicket`;
        const postData: object = {
            assessmentRunId,
            fileName,
            fileSize,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },
    async transferGradesToExternalSystem(
        assessmentRunId: number,
        validateOnly: boolean,
        committeeId?: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}grading/TransferGradesToExternalSystem`;
        const postData: object = committeeId && committeeId > 0
            ? {
                assessmentRunId,
                committeeId,
                validateOnly,
            }
            : {
                assessmentRunId,
                validateOnly,
            };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },

    async transferGradesToLadok(
        assessmentRunId: number,
        signal?: AbortSignal,
    ) {
        const url = `${apiRoots.ASYNC}SubmitTask`;
        const postData: object = {
            repository: 'assessment',
            action: 'ExportAssessmentRunResultsToOneRosterXml',
            parametersJSON: JSON.stringify({
                assessmentRunId,
            }),
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },

    async getGradingStatusAndActions(
        assessmentRunId: number,
        committeeId: number,
        signal?: AbortSignal
    ) {
        let url = `${apiRoots.GRADING}GetGradingStatusAndActions?assessmentRunId=${assessmentRunId}`;
        if (committeeId && committeeId > 0) {
            url += `&committeeId=${committeeId}`;
        }

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getCompetenceLevels(
        assessmentRunId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}GetCompetenceLevels?assessmentRunId=${assessmentRunId}`;
        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async emulateCompetenceLevels(
        assessmentRunId: number,
        competenceLevels: string,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.ASSESSMENT}EmulateCompetenceLevels`;
        const postData = {
            assessmentRunId,
            competenceLevels,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },

    async confirmOwnGrading(
        assessmentRunId: number,
        approved: boolean,
        committeeId: number,
        approveOnBehalf: boolean,
        confirmFinalGrades: boolean,
        signal?: AbortSignal
    ) {
        const url = confirmFinalGrades
            ? `${apiRoots.ASSESSMENT}ApproveFinalGrades`
            : `${apiRoots.ASSESSMENT}ApproveYourGrades`;
        let postData = {
            assessmentRunId,
        };

        if (committeeId && committeeId > 0) {
            postData = { ...postData, ...{ committeeId } };
        }

        if (approveOnBehalf) {
            postData = { ...postData, ...{ approveOnBehalf } };
        }

        if (confirmFinalGrades) {
            // approve final grades api uses approve parameter
            postData = { ...postData, ...{ approve: approved } };
        } else {
            postData = { ...postData, ...{ approved } };
        }

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(
                url,
                postData,
                signal
            )
        );
    },

    async setCandidateLevelConfirmationStatus(
        assessmentRunId: number,
        partyId: number,
        status: string,
        onBehalf?: boolean,
    ) {
        const url = `${apiRoots.ASSESSMENT}SetCandidateLevelConfirmationStatus`;
        const postData = {
            assessmentRunId,
            partyId,
            onBehalf,
            status,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },
    async GetStudentReportInfo(
        assessmentRunId: number,
        partyId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.GRADING}GetStudentReportInfo?assessmentRunId=${assessmentRunId}&partyId=${partyId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },
    async getQuestionsForAnswerKeyCorrection(assessmentRunId: number, signal?: AbortSignal) {
        const url = `${apiRoots.GRADING}GetQuestionsForAnswerKeyCorrection?assessmentRunId=${assessmentRunId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getAssessmentRunSession(assessmentRunId: number, assessmentRunSessionId?: number, signal?: AbortSignal) {
        let url = `${apiRoots.ASSESSMENT}GetAssessmentRunSession?assessmentRunId=${assessmentRunId}`;

        if (!isNullOrUndefined(assessmentRunSessionId)) {
            url = `${url}&assessmentRunSessionId=${assessmentRunSessionId}`;
        }

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async updateAssessmentRunSession(
        assessmentRunSessionId: number,
        optionalParams?: { status?: AssessmentRunSessionStatus; questionId?: number; reloadStartTime?: boolean },
        signal?: AbortSignal
    ) {
        if (!(assessmentRunSessionId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const { status, questionId, reloadStartTime } = optionalParams;
        const url = `${apiRoots.ASSESSMENT}UpdateAssessmentRunSession`;

        let postData = {
            assessmentRunSessionId,
        };

        if (!isNullOrUndefined(status)) {
            postData = { ...postData, ...{ status } };
        }

        if (!isNullOrUndefined(questionId)) {
            postData = { ...postData, ...{ questionId } };
        }

        if (!isNullOrUndefined(reloadStartTime)) {
            postData = { ...postData, ...{ reloadStartTime } };
        }

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData, signal)
        );
    },

    async fetchQuestionSet(assessmentRunId: number, context: string, signal?: AbortSignal, partyId?: number) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        let url = `${apiRoots.TESTPLAYER}GetQuestionSet?assessmentRunId=${assessmentRunId}&context=${context}`;

        if (partyId > 0) {
            url = `${url}&partyId=${partyId}`;
        }

        return requestService.executeRequest(requestService.createFetchRequestObject(url, signal));
    },

    async getQuestion(assessmentRunId: number, contentRevisionId: number, viewAsStudent: boolean, signal?: AbortSignal) {
        if (!(assessmentRunId > 0 && contentRevisionId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        let url = `${apiRoots.TESTPLAYER}GetQuestion?assessmentRunId=${assessmentRunId}&contentRevisionId=${contentRevisionId}`;

        if (viewAsStudent) {
            url = `${url}&viewAs=candidate`;
        }

        return requestService.executeRequest(requestService.createFetchRequestObject(url, signal));
    },

    async storeAdaptiveAssessmentRunForSpeakingSession(
        assessmentRunSessionId: number,
        aarJson: string,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunSessionId > 0 && isValidJsonString(aarJson))) {
            return handleInvalidArguments(...arguments);
        }

        const url = `${apiRoots.ASSESSMENT}StoreAdaptiveAssessmentRunForSpeakingSession`;
        const data = {
            assessmentRunSessionId,
            aarJson,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, data, signal)
        );
    },

    async getAdaptiveAssessmentRun(
        assessmentRunId: number,
        partyId: number,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        const url = `${apiRoots.ASSESSMENT}GetAdaptiveAssessmentRun?assessmentRunId=${assessmentRunId}&partyId=${partyId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async setIdVerificationStatus(
        assessmentRunId: number,
        userId: number,
        idVerificationStatus: string,
        signal?: AbortSignal,
        idVerificationStatusSubTypes?: string[],
        idVerificationStatusOtherText?: string,
    ) {
        const url = `${apiRoots.ASSESSMENT}SetIDVerificationStatus`;

        let postData: { [key: string]: any } = {
            assessmentRunId,
            userId,
            idVerificationStatus,
        };

        if (idVerificationStatusSubTypes?.length) {
            postData = { ...postData, idVerificationStatusSubTypes: JSON.stringify(idVerificationStatusSubTypes) };
        }

        if (idVerificationStatusOtherText) {
            postData = { ...postData, idVerificationStatusOtherText };
        }

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData, signal)
        );
    },

    async getIdVerificationStatus(
        assessmentRunId: number,
        userId: number,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunId > 0 && userId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        const url = `${apiRoots.ASSESSMENT}GetIDVerificationStatus?assessmentRunId=${assessmentRunId}&userId=${userId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getManualIdVerificationInfo(
        assessmentRunId: number,
        userId: number,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunId > 0 && userId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        const url = `${apiRoots.ASSESSMENT}GetManualIDVerificationInfo?assessmentRunId=${assessmentRunId}&userId=${userId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getSpeakingScore(
        assessmentRunSessionId: number,
        partyId: number,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunSessionId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        const url = `${apiRoots.GRADING}GetIELTSSpeakingScore?assessmentRunSessionId=${assessmentRunSessionId}&partyId=${partyId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async setIncidentEvent(
        assessmentRunId: number,
        partyId: number,
        flaggingJSON: FlaggingEvent[] | FlaggingEvent,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        let flaggingJSONParam;
        if (Array.isArray(flaggingJSON)) {
            flaggingJSONParam = flaggingJSON.map((obj: FlaggingEvent) => {
                return `flaggingJSON=${encodeURIComponent(JSON.stringify(obj))}`;
            }).join('&');
        } else {
            flaggingJSONParam = `flaggingJSON=${encodeURIComponent(JSON.stringify(flaggingJSON))}`;
        }
        const url = `${apiRoots.ASSESSMENT}AddCandidateFlaggingEvent?${flaggingJSONParam}`;

        const postData = {
            assessmentRunId,
            partyId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData, signal)
        );
    },
    async setSpeakingScore(
        assessmentRunSessionId: number,
        partyId: number,
        testCompleted: boolean,
        scoreDetails?: RatingControl[],
        signal?: AbortSignal
    ) {
        if (!(assessmentRunSessionId > 0 && partyId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        const url = `${apiRoots.GRADING}SetIELTSSpeakingScore`;

        const postData = {
            assessmentRunSessionId,
            partyId,
            testCompleted,
            scoreDetails: JSON.stringify(scoreDetails),
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData, signal)
        );
    },

    async updateAssessmentRunSessionContributor(
        assessmentRunSessionId: number,
        partyId: number,
        contextDisplayStatus: ContextDisplayStatus,
        signal?: AbortSignal
    ) {
        if (!(assessmentRunSessionId > 0 && partyId > 0 && !isNullOrUndefined(contextDisplayStatus))) {
            return handleInvalidArguments(...arguments);
        }

        const url = `${apiRoots.ASSESSMENT}UpdateAssessmentRunSessionContributor`;
        const postData = {
            assessmentRunSessionId,
            partyId,
            contextDisplayStatus,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData, signal)
        );
    },

    async removeSubmission(assessmentRunId: number, signal?: AbortSignal) {
        if (assessmentRunId <= 0) {
            return handleInvalidArguments(...arguments);
        }

        const url = `${apiRoots.DASHBOARD}DeleteSubmission`;

        const postData = {
            assessmentRunId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData, signal)
        );
    },

    async refreshHeartbeat(
        assessmentRunId: number,
        signal?: AbortSignal
    ) {
        const url = `${apiRoots.GRADING}RefreshHeartbeat`;
        const postData = {
            assessmentRunId,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData, signal)
        );
    },

    async getGradingPartyIds(assessmentRunId: number, selectedCommitteeId?: number, signal?: AbortSignal) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        let url = `${apiRoots.GRADING}GetGradingPartyIds?assessmentRunId=${assessmentRunId}`;

        if (selectedCommitteeId > 0) {
            url = `${url}&committeeId=${selectedCommitteeId}`;
        }

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async getAssessmentRunFeedback(assessmentRunId: number, signal?: AbortSignal) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}GetAssessmentRunFeedback?assessmentRunId=${assessmentRunId}`;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url, signal)
        );
    },

    async saveAssessmentRunFeedback(assessmentRunId: number, feedbackContent: string, signal?: AbortSignal) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }

        const url = `${apiRoots.GRADING}StoreAssessmentRunFeedback`;
        const postData = {
            assessmentRunId,
            feedbackContent,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData, signal)
        );
    },

    async getUploadTicketForAssessmentRunFeedbackAttachment(
        mimeType: string,
        fileName: string,
        fileSize: number,
        assessmentRunId: number
    ) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}GetUploadTicketForFeedbackAttachment`;
        const postData: object = {
            assessmentRunId,
            fileName,
            fileSize,
            mimeType,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async removeAssessmentRunFeedbackAttachment(
        assessmentRunId: number,
        feedbackId: number,
        attachmentId: number
    ) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}RemoveAssessmentRunFeedbackAttachment`;
        const postData: object = {
            assessmentRunId,
            feedbackId,
            attachmentId,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async uploadAssessmentRunFeedbackAttachmentSuccess(
        assessmentRunId: number,
        ticket: string
    ) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}UploadAssessmentRunFeedbackSuccess`;
        const postData: object = {
            assessmentRunId,
            ticket,
        };
        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },
    async lockAssessmentRunFeedback(assessmentRunId: number, lockItem: boolean, feedbackId?: number,) {
        if (!(assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}LockAssessmentRunFeedback`;
        let postData: object = {
            assessmentRunId,
            lockItem,
            objectId: assessmentRunId,
        };

        if (feedbackId) {
            postData = {
                ...postData,
                feedbackId,
            };
        }

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },
    getAttachedFilesInfo(assessmentRunId: number, questionId?: string): Promise<{ questionIds: string[]; partyIds: string[] }> {
        let url = `${apiRoots.GRADING}GetAttachedFilesInfo?assessmentRunId=${assessmentRunId}`;
        url = questionId ? `${url}&questionId=${questionId}` : url;

        return requestService.executeRequest(
            requestService.createFetchRequestObject(url)
        );
    },

    async setQuestionValidationIssueReviewed(
        assessmentRunId: number,
        partyId: number,
        questionId: number,
        maximaValidationIssueReviewed: boolean
    ) {
        if (!(questionId > 0 && partyId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}SetQuestionMaximaValidationIssueReviewed`;
        const postData: object = {
            assessmentRunId,
            partyId,
            questionId,
            maximaValidationIssueReviewed,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },

    async setFlagCandidateForNextAttempt(
        assessmentRunId: number,
        partyId: number,
        nextAttemptAllowed: boolean
    ) {
        if (!(partyId > 0 && assessmentRunId > 0)) {
            return handleInvalidArguments(...arguments);
        }
        const url = `${apiRoots.GRADING}FlagCandidateForNextAttempt`;
        const postData: object = {
            assessmentRunId,
            partyId,
            nextAttemptAllowed,
        };

        return requestService.executeRequest(
            requestService.createFormDataPostRequestObject(url, postData)
        );
    },
};

export default apiService;
